import React from 'react';
import {printPalette, printTypography} from './printPalette';

export interface BillOfLadingData {
    blNumber: string;
    refNumber: string;
    voyageNumber?: string;
    shipper: {
        name: string;
        address?: string;
    };
    consignee: {
        name: string;
        address?: string;
    };
    notifyAddress?: string;
    preCarriagePlace?: string;
    vesselName: string;
    portOfLoading: string;
    portOfDischarge: string;
    deliveryPlace?: string;
    marksAndNumbers: string;
    numberOfUnits: string;
    description: string;
    grossWeight: string;
    measurement: string;
    freightPayableAt: string;
    placeOfIssue: string;
    dateOfIssue: string;
    numberOfOriginalBLs: string;
    isOriginal: boolean;
    loaded: boolean;
}

interface BillOfLadingProps extends BillOfLadingData {
}

const BillOfLading: React.FC<BillOfLadingProps> = (props) => {
    const {
        blNumber,
        shipper,
        consignee,
        vesselName,
        voyageNumber,
        portOfLoading,
        portOfDischarge,
        marksAndNumbers,
        numberOfUnits,
        description,
        grossWeight,
        measurement,
        freightPayableAt,
        placeOfIssue,
        dateOfIssue,
        numberOfOriginalBLs,
        isOriginal,
        loaded,
    } = props;

    const documentType = !loaded ? 'D R A F T' : (isOriginal ? 'O R I G I N A L' : 'C O P Y');
    const borderColor = printPalette.primary;

    return (
        <div style={{
            width: '210mm',
            minHeight: '297mm',
            margin: '0 auto',
            background: 'white',
            padding: '10mm',
            fontFamily: `${printTypography.baseFamily}, Arial, sans-serif`,
            fontSize: '9px',
            lineHeight: 1.2,
            color: printPalette.text,
            boxSizing: 'border-box',
        }}>
            {/* Top Section - Two Halves */}
            <div style={{display: 'flex', border: `1px solid ${borderColor}`}}>
                {/* LEFT HALF - Form Fields */}
                <div style={{flex: 1, borderRight: `1px solid ${borderColor}`}}>
                    {/* B/L No. Row */}
                    <div style={{display: 'flex', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            width: '50%',
                            padding: '6px 8px',
                            borderRight: `1px solid ${borderColor}`,
                            fontWeight: 'bold',
                            fontSize: '12px',
                            color: printPalette.primaryDark,
                            backgroundColor: printPalette.headerBg,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start'
                        }}>BL No.
                        </div>
                        <div style={{
                            width: '50%',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: 'bold'
                        }}>{blNumber}</div>
                    </div>

                    {/* Shipper - 5 lines height */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Shipper
                        </div>
                        <div style={{fontSize: '11px', padding: '4px 0', minHeight: '60px'}}>
                            {shipper.name}
                            {shipper.address && shipper.address.split('\n').map((line, i) => (
                                <div key={i}>{line}</div>
                            ))}
                        </div>
                    </div>

                    {/* Consignee - 5 lines height, same as Shipper */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Consignee
                        </div>
                        <div style={{fontSize: '11px', padding: '4px 0', minHeight: '60px'}}>
                            {consignee.name}
                            {consignee.address && consignee.address.split('\n').map((line, i) => (
                                <div key={i}>{line}</div>
                            ))}
                        </div>
                    </div>

                    {/* Notify - half size of shipper/consignee */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Notify
                        </div>
                        <div style={{fontSize: '10px', padding: '2px 0', minHeight: '30px'}}>Same as Consignee</div>
                    </div>

                    {/* Ocean Vessel */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Ocean Vessel
                        </div>
                        <div style={{fontSize: '11px', padding: '4px 0', minHeight: '20px'}}>{vesselName}{voyageNumber ? ` [${voyageNumber}]` : ''}</div>
                    </div>

                    {/* Port of Loading */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Port of Loading
                        </div>
                        <div style={{fontSize: '11px', padding: '4px 0', minHeight: '20px'}}>{portOfLoading}</div>
                    </div>

                    {/* Port of Discharge */}
                    <div style={{padding: '4px 6px', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            color: printPalette.primaryDark
                        }}>Port of Discharge
                        </div>
                        <div style={{fontSize: '11px', padding: '4px 0', minHeight: '20px'}}>{portOfDischarge}</div>
                    </div>
                </div>

                {/* RIGHT HALF - Branding */}
                <div style={{
                    width: '50%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px',
                    backgroundColor: printPalette.headerBg,
                    borderBottom: `1px solid ${borderColor}`
                }}>
                    {/* Title */}
                    <div style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        fontStyle: 'italic',
                        color: printPalette.primary,
                        marginBottom: '12px'
                    }}>BILL OF LADING
                    </div>

                    {/* Logo */}
                    <div style={{margin: '10px 0'}}>
                        <img src="/fms_logo.png" alt="Logo" style={{width: '150px', height: 'auto'}}/>
                    </div>

                    {/* Company Info */}
                    <div style={{textAlign: 'center', marginTop: '10px'}}>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: printPalette.primary,
                            marginBottom: '5px'
                        }}>CLAPOTIS CARRIERS LTD
                        </div>
                        <div style={{fontSize: '12px', fontWeight: 'bold'}}>VAN DYKE STREET</div>
                        <div style={{fontSize: '12px', fontWeight: 'bold'}}>BEIRUT LEBANON</div>
                        <div style={{
                            fontSize: '11px',
                            marginTop: '8px',
                            color: printPalette.primaryDark
                        }}>info@clapotismar.com
                        </div>
                    </div>
                </div>
            </div>

            {/* Goods Table */}
            <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: `1px solid ${borderColor}`,
                borderTop: 'none'
            }}>
                <thead>
                <tr>
                    <th style={{
                        border: `1px solid ${borderColor}`,
                        borderTop: 'none',
                        padding: '8px 4px',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        verticalAlign: 'middle',
                        backgroundColor: printPalette.headerBg,
                        color: printPalette.primaryDark,
                        width: '12%'
                    }}>Marks & Nos.
                    </th>
                    <th style={{
                        border: `1px solid ${borderColor}`,
                        borderTop: 'none',
                        padding: '8px 4px',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        verticalAlign: 'middle',
                        backgroundColor: printPalette.headerBg,
                        color: printPalette.primaryDark,
                        width: '8%'
                    }}>Quantity
                    </th>
                    <th style={{
                        border: `1px solid ${borderColor}`,
                        borderTop: 'none',
                        padding: '8px 4px',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        verticalAlign: 'middle',
                        backgroundColor: printPalette.headerBg,
                        color: printPalette.primaryDark,
                        width: '40%'
                    }}>Description of Goods
                    </th>
                    <th style={{
                        border: `1px solid ${borderColor}`,
                        borderTop: 'none',
                        padding: '8px 4px',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        verticalAlign: 'middle',
                        backgroundColor: printPalette.headerBg,
                        color: printPalette.primaryDark,
                        width: '15%'
                    }}>Weight
                    </th>
                    <th style={{
                        border: `1px solid ${borderColor}`,
                        borderTop: 'none',
                        padding: '8px 4px',
                        textAlign: 'left',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        verticalAlign: 'middle',
                        backgroundColor: printPalette.headerBg,
                        color: printPalette.primaryDark,
                        width: '15%'
                    }}>Measurement
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td style={{
                        border: `1px solid ${borderColor}`,
                        borderBottom: 'none',
                        padding: '6px',
                        verticalAlign: 'top',
                        height: '220px',
                        fontSize: '11px'
                    }}>
                        <div style={{fontSize: '9px', marginBottom: '4px'}}>CHASSIS NO.</div>
                        <div>
                            {marksAndNumbers.replace('CH. NO. ', '').split('\n').map((line, i) => (
                                <div key={i}>{line}</div>
                            ))}
                        </div>
                    </td>
                    <td style={{
                        border: `1px solid ${borderColor}`,
                        borderBottom: 'none',
                        padding: '6px',
                        verticalAlign: 'top',
                        height: '220px',
                        fontSize: '11px'
                    }}>
                        <div style={{fontSize: '9px', marginBottom: '4px'}}>&nbsp;</div>
                        <div>{numberOfUnits.replace(' used vehicle', '')}</div>
                    </td>
                    <td style={{
                        border: `1px solid ${borderColor}`,
                        borderBottom: 'none',
                        padding: '6px',
                        verticalAlign: 'top',
                        height: '220px',
                        fontSize: '11px'
                    }}>
                        <div style={{fontSize: '9px', marginBottom: '4px'}}>USED VEHICLE{parseInt(numberOfUnits) > 1 ? 'S' : ''}</div>
                        <div>
                            {description.split('\n').map((line, i) => (
                                <div key={i}>{line}</div>
                            ))}
                        </div>
                    </td>
                    <td style={{
                        border: `1px solid ${borderColor}`,
                        borderBottom: 'none',
                        padding: '6px',
                        verticalAlign: 'top',
                        height: '220px',
                        fontSize: '11px'
                    }}>
                        <div style={{fontSize: '9px', marginBottom: '4px'}}>&nbsp;</div>
                        <div>{grossWeight}</div>
                    </td>
                    <td style={{
                        border: `1px solid ${borderColor}`,
                        borderBottom: 'none',
                        padding: '6px',
                        verticalAlign: 'top',
                        height: '220px',
                        fontSize: '11px'
                    }}>
                        <div style={{fontSize: '9px', marginBottom: '4px'}}>&nbsp;</div>
                        <div>{measurement}</div>
                    </td>
                </tr>
                </tbody>
            </table>

            {/* Disclaimer Row */}
            <div style={{display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none'}}>
                <div style={{
                    flex: 1,
                    padding: '6px',
                    fontSize: '7px',
                    lineHeight: 1.4,
                    borderRight: `1px solid ${borderColor}`
                }}>
                    <p>The carrier is not responsible or in any way liable for the conditions of the outer as well as
                        the Inner parts of the vehicle(s) including all mechanical/ electrical parts and furthermore
                        declines any responsibility for any damage which may occur during loading/unloading operations
                        on account of the malfunctioning of the vehicle(s).</p>
                    <p style={{marginTop: '4px'}}>Carrier not responsible for scratches, dents and alleged missing
                        items.</p>
                </div>
                <div style={{
                    width: '180px',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    borderRight: `1px solid ${borderColor}`,
                    backgroundColor: printPalette.rowAltBg
                }}>
                    <p>The carrier is entitled to apply the full tariff in event of misdeclaration of cargo</p>
                </div>
                <div style={{flex: 1, padding: '6px', fontSize: '7px', lineHeight: 1.4}}>
                    <p>The goods hereby acknowledged are unprotected and all the carriers rights and immunities in the
                        even of loss or damage by reason of that in fact are hereby expressly reserved.</p>
                    <p style={{marginTop: '4px'}}>Vessel not responsible for accessories and/or other goods left inside
                        vehicles nor any interior damage to upholstery fittings or accessories.</p>

                </div>
            </div>

            {/* Above Particulars Declared */}
            <div style={{
                border: `1px solid ${borderColor}`,
                borderTop: 'none',
                padding: '8px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '13px',
                backgroundColor: printPalette.headerBg,
                color: printPalette.primary
            }}>
                ABOVE PARTICULARS DECLARED BY SHIPPER
            </div>

            {/* Bottom Terms Section */}
            <div style={{display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none'}}>
                <div style={{
                    flex: 1,
                    padding: '6px',
                    fontSize: '7px',
                    lineHeight: 1.5,
                    borderRight: `1px solid ${borderColor}`
                }}>
                    <p>GOODS SHIPPED on board the Vessel in apparent good order and condition, weight, measure, marks &
                        numbers, quality, contents and value unknown unless specified herein, for carriage to the port
                        of destination or so near thereto as the vessel may safely get and lie always afloat, to be
                        delivered in the like good order and condition at the aforesaid port to Consignees or their
                        Assigns. Freight as per note on the margin plus other charges incurred in accordance with the
                        provisions contained in this Bill of Lading - unless prepaid - to be paid by Consignees or their
                        Assigns.</p>
                    <p style={{marginTop: '4px'}}>In accepting this Bill of Lading the Merchant expressly accepts and
                        agrees to all it's stipulations on both pages, whether written, printed, stamped or otherwise
                        incorporated, as fully as if they were all signed by the Merchant.</p>
                    <p style={{marginTop: '4px'}}>One original Bill of Lading must be surrendered duly endorsed in
                        exchange for the goods or delivery order.</p>
                    <p style={{marginTop: '5px', textTransform: 'uppercase'}}>THE STOWING OF UNMANIFESTED GOODS INSIDE
                        VEHICLES IS PROHIBITED BY THE AUTHORITIES AT THE PORT OF DISCHARGE, AND ARE LIABLE TO FORFEITURE
                        BY CUSTOMS. IN THE EVENT OF A BREACH OF THIS RULE ANY FINES LEVIED AGAINST THE CARRIER ARE FOR
                        THE ACCOUNT OF THE MERCHANT, NOR WILL THE CARRIER ACCEPT ANY LIABILITY IN RESPECT OF THEM</p>
                    <p style={{marginTop: '5px', textTransform: 'uppercase'}}>SHIPPERS DECLARE THAT LINE, SHIP, SHIPPING
                        COMPANY AND THEIR AGENTS ARE NOT RESPONSIBLE FOR FINES, PENALTIES AND/OR ANY OTHER CONSEQUENCES
                        THAT MAY RESULT FROM ERRONEOUSLY SHIPPING AND OVERAGED VEHICLE.</p>
                    <p style={{marginTop: '5px'}}><strong>AGREEMENT</strong> - IF CARGO HAS NOT BEEN COLLECTED WITHIN 30
                        DAYS AFTER DATE OF ISSUE OF THIS B/L, WE ARE AUTHORIZED PUT CARGO FORSALE TO COVER OUR EXPENSES
                        IN RESPECT OF FREIGHT, CHARGES AND DEMURRAGE !</p>
                    <p style={{marginTop: '6px', fontWeight: 'bold'}}>FREIGHT PAYABLE CARGO LOST OR NOT LOST</p>
                    <p>* Applicable only when document used as Through Bill of Lading</p>
                </div>
                <div style={{width: '50%', display: 'flex', flexDirection: 'column'}}>
                    <div style={{
                        flex: 1,
                        padding: '6px',
                        fontSize: '8px',
                        lineHeight: 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <p>IN WITNESS where of the Master of the said Vessel has signed . . .</p>
                        <p>Bills of Lading all of this tenor and date, one of which being accomplished, the other(s) to
                            stand void.</p>
                    </div>
                    {/* ORIGINAL/COPY Box */}
                    <div style={{
                        flex: 1,
                        padding: '8px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        borderBottom: `1px solid ${borderColor}`,
                        color: printPalette.primary,
                        backgroundColor: printPalette.headerBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {documentType}
                    </div>
                    <div style={{flex: 1, display: 'flex', borderBottom: `1px solid ${borderColor}`}}>
                        <div style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                            borderRight: `1px solid ${borderColor}`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                color: printPalette.primaryDark
                            }}>Freight payable at
                            </div>
                            <div style={{fontSize: '13px'}}>{freightPayableAt}</div>
                        </div>
                        <div style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <div
                                style={{fontWeight: 'bold', marginBottom: '4px', color: printPalette.primaryDark}}>Place
                                and Date of Issue
                            </div>
                            <div style={{fontSize: '13px'}}>{placeOfIssue} &nbsp; {dateOfIssue}</div>
                        </div>
                    </div>
                    <div style={{flex: 1, display: 'flex'}}>
                        <div style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                            borderRight: `1px solid ${borderColor}`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                color: printPalette.primaryDark
                            }}>Number of original Bs/l
                            </div>
                            <div style={{fontSize: '13px'}}>{numberOfOriginalBLs}</div>
                        </div>
                        <div style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                color: printPalette.primaryDark
                            }}>Signature
                            </div>
                            <div style={{fontSize: '12px', textAlign: 'center', marginTop: '6px'}}>
                                <div>CLAPOTIS CARRIERS LTD</div>
                                <div style={{fontSize: '10px'}}>- AS AGENT ONLY -</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillOfLading;
